/**
 * Generic case conversion utilities for API request/response transforms.
 * Eliminates field-by-field dual-get patterns (THUB-27).
 *
 * Only converts object KEYS — values are left unchanged.
 */

function toCamelCase(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
}

/**
 * Recursively convert all object keys from snake_case to camelCase.
 * Handles nested objects, arrays, null/undefined, Date objects, and primitives.
 */
export function snakeToCamel<T = any>(obj: unknown): T {
  if (obj === null || obj === undefined) return obj as T;
  if (obj instanceof Date) return obj as T;
  if (Array.isArray(obj)) return obj.map(snakeToCamel) as unknown as T;
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[toCamelCase(key)] = snakeToCamel(value);
    }
    return result as T;
  }
  return obj as T;
}

/**
 * Recursively convert all object keys from camelCase to snake_case.
 * Handles nested objects, arrays, null/undefined, Date objects, and primitives.
 */
export function camelToSnake<T = any>(obj: unknown): T {
  if (obj === null || obj === undefined) return obj as T;
  if (obj instanceof Date) return obj as T;
  if (Array.isArray(obj)) return obj.map(camelToSnake) as unknown as T;
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[toSnakeCase(key)] = camelToSnake(value);
    }
    return result as T;
  }
  return obj as T;
}
